import { Injectable } from '@angular/core';

import { filter, forkJoin, map, mergeMap, switchMap, take } from 'rxjs';

import { GraphEndpoint, HandleSimpleRequest, TaBaseService } from '@ta/server';
import { isNonNullable } from '@ta/utils';

import {
  Notification,
  notificationMetadataPropsComposition,
  notificationProps,
} from './dto/notification';
import { deleteNotification, markAllNotificationsAsRead, markNotificationAsRead } from './mutation';
import { notifications, unreadNotificationCount } from './queries';

const notificationListProps = `
  ${notificationProps.get('id')}
  ${notificationProps.get('key')}
  ${notificationProps.get('isRead')}
  ${notificationProps.get('createdDate')}
  ${notificationMetadataPropsComposition}
`;
const clearCacheKey = ['notifications', 'unreadNotificationCount'];
const graphEndpoint: GraphEndpoint = {
  clientName: 'notificationService',
  endpoint: '',
};

@Injectable({
  providedIn: 'root',
})
export class NotificationService extends TaBaseService {
  public notifications = new HandleSimpleRequest<Notification[]>();
  public unreadNotifications = new HandleSimpleRequest<Notification[]>();
  public dashboardNotifications = new HandleSimpleRequest<Notification[]>();
  public unreadCount = new HandleSimpleRequest<number>();

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public fetchNotifications$(onlyUnread: boolean = false) {
    const target = onlyUnread ? this.unreadNotifications : this.notifications;

    return target.fetch(
      this._graphService
        .fetchQueryBuilder<Notification[]>(
          notifications({
            where: onlyUnread ? { isRead: { eq: false } } : undefined,
            order: { createdDate: 'DESC' },
            props: notificationListProps,
          }),
          graphEndpoint.clientName,
        )
        .pipe(filter(isNonNullable)),
    );
  }

  public fetchDashboardNotifications$() {
    return this.dashboardNotifications.fetch(
      this._graphService
        .fetchQueryBuilder<Notification[]>(
          notifications({
            where: { isRead: { eq: false } },
            order: { createdDate: 'DESC' },
            props: notificationListProps,
          }),
          graphEndpoint.clientName,
        )
        .pipe(
          filter(isNonNullable),
          map((list) => list.slice(0, 5)),
        ),
    );
  }

  public fetchUnreadCount$() {
    return this.unreadCount.fetch(
      this._graphService
        .fetchQueryBuilder<number>(unreadNotificationCount(), graphEndpoint.clientName)
        .pipe(filter(isNonNullable)),
    );
  }

  public markAsRead$(notificationId: string) {
    return this._graphService
      .mutate<Notification>(
        markNotificationAsRead(notificationId),
        'markNotificationAsRead',
        graphEndpoint.clientName,
        clearCacheKey,
      )
      .pipe(mergeMap(() => this.refetchAll$()));
  }

  public markAllAsRead$() {
    return this._graphService
      .mutate<number>(
        markAllNotificationsAsRead(),
        'markAllNotificationsAsRead',
        graphEndpoint.clientName,
        clearCacheKey,
      )
      .pipe(mergeMap(() => this.refetchAll$()));
  }

  public delete$(notificationId: string) {
    return this._mutateDeleteOne$(notificationId).pipe(mergeMap(() => this.refetchAll$()));
  }

  public deleteAll$() {
    return this.notifications.get$().pipe(
      take(1),
      filter(isNonNullable),
      switchMap((list) => forkJoin(list.map((n) => this._mutateDeleteOne$(n.id)))),
      mergeMap(() => this.refetchAll$()),
    );
  }

  private _mutateDeleteOne$(notificationId: string) {
    return this._graphService.mutate<Notification>(
      deleteNotification(notificationId),
      'deleteNotification',
      graphEndpoint.clientName,
      clearCacheKey,
    );
  }

  /** Après une mutation ou un push reçu : listes, tableau de bord et compteur. */
  public refetchAll$() {
    return forkJoin({
      notification: this.fetchNotifications$(),
      unreadnotification: this.fetchNotifications$(true),
      dashboard: this.fetchDashboardNotifications$(),
      count: this.fetchUnreadCount$(),
    });
  }
}
